import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  CardBody,
  Row,
  Col,
  Card,
  Container,
  CardHeader,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalBody,
  Label,
  Input,
  Button,
} from 'reactstrap';
import DataTable from 'react-data-table-component';
import BreadCrumb from '../../Components/Common/BreadCrumb';
import DeleteModal from '../../Components/Common/DeleteModal';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import { api } from '../../config';
import { getAuthToken } from '../../auth/tokenvalidation';

const DocumentUploadMasterList = () => {
  const token = getAuthToken();

  const [data, setData] = useState({ items: [] });
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteModalMulti, setDeleteModalMulti] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [errors, setErrors] = useState({});
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [modalDocumentUpload, setModalDocumentUpload] = useState(false);
  const [documentUploadFormData, setDocumentUploadFormData] = useState({
    documentName: null,
    description: null,
    documentType: 1,
    id: null,
    path: null,
  });

  const [filterText, setFilterText] = useState('');
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDocumentType, setSelectedDocumentType] = useState('all');
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  // Document type options for dropdown
  const documentTypeOptions = [
    { value: 1, label: 'Procurement' },
    { value: 2, label: 'HPS' },
  ];

  const handleDeleteModal = id => {
    const selectedRow = data.items.find(item => item.id === id);
    setInvoice(selectedRow);
    setDeleteModal(true);
  };

  const handleDeleteInvoice = async () => {
    if (invoice) {
      try {
        const response = await fetch(
          `${api.API_URL}/document-upload/${invoice.id}`,
          {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            errorData.message || `HTTP error! status: ${response.status}`
          );
        }

        toast.success('Document Upload Master berhasil dihapus!', {
          autoClose: 3000,
        });
        setDeleteModal(false);
        setInvoice(null);
        fetchData(currentPage, rowsPerPage, token);
      } catch (err) {
        toast.error(`Gagal menghapus document upload master: ${err.message}`, {
          autoClose: 3000,
        });
      }
    }
  };

  const deleteMultiple = () => {
    // Implement multiple delete functionality
    // This would typically involve deleting selected items
    // For now, just close the modal
    setDeleteModalMulti(false);
  };

  const getDocumentTypeLabel = type => {
    const option = documentTypeOptions.find(opt => opt.value === type);
    return option ? option.label : 'Unknown';
  };

  // const handleViewPath = filePath => {
  //   if (filePath) {
  //     window.open(filePath, '_blank', 'noopener,noreferrer');
  //   } else {
  //     toast.info('No file available to view');
  //   }
  // };

  const handleViewPath = filePath => {
    if (!filePath) {
      toast.info('No file available for this document');
      return;
    }
    const link = document.createElement('a');
    link.href = filePath;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const columns = [
    {
      name: (
        <span className='form-label text-uppercase fw-semibold fs-13'>
          Document Name
        </span>
      ),
      selector: row => row.documentName,
      sortable: true,
      width: '35%',
      wrap: true,
      cell: row => {
        return (
          <>
            <h6 className={'mb-0'}>{row.documentName}</h6>
          </>
        );
      },
    },
    {
      name: (
        <span className='form-label text-uppercase fw-semibold fs-13'>
          Description
        </span>
      ),
      selector: row => row.description,
      sortable: true,
      width: '40%',
      wrap: true,
      cell: row => {
        return (
          <>
            <h6 className={'mb-0'}>{row.description}</h6>
          </>
        );
      },
    },
    {
      name: (
        <span className='form-label text-uppercase fw-semibold fs-13'>
          Document Type
        </span>
      ),
      selector: row => row.documentType,
      sortable: true,
      width: '15%',
      wrap: true,
      cell: row => {
        return (
          <>
            <span
              className={`badge ${row.documentType === 1 ? 'bg-primary' : 'bg-success'}`}
            >
              {getDocumentTypeLabel(row.documentType)}
            </span>
          </>
        );
      },
    },
    {
      name: (
        <span className='form-label text-uppercase fw-semibold fs-13'>
          Action
        </span>
      ),
      width: '10%',
      center: true,
      sortable: false,
      className: 'text-end',
      cell: row => (
        <div className='d-flex gap-1 justify-content-end'>
          <button
            className='btn btn-soft-success btn-sm btn-icon'
            onClick={() => handleViewPath(row.path)}
          >
            <i className='ri-eye-fill align-bottom'></i>
          </button>
          <button
            className='btn btn-soft-info btn-sm btn-icon'
            onClick={() => handleUpdateModal(row.id)}
          >
            <i className='ri-pencil-fill align-bottom'></i>
          </button>
          <button
            className='btn btn-soft-danger btn-sm btn-icon'
            onClick={() => handleDeleteModal(row.id)}
          >
            <i className='ri-delete-bin-6-line align-bottom'></i>
          </button>
        </div>
      ),
    },
  ];

  document.title = 'Data Document Upload Master';

  const handleChange = e => {
    const { name, value } = e.target;
    setDocumentUploadFormData({
      ...documentUploadFormData,
      [name]: name === 'documentType' ? parseInt(value) : value,
    });
  };

  const fetchData = useCallback(
    async (currentPage, rowsPerPage, token) => {
      setLoading(true);
      try {
        // Use the single GET endpoint for all data
        const apiUrl = `${api.API_URL}/document-upload?PageNumber=${currentPage}&PageSize=${rowsPerPage}`;

        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          credentials: 'same-origin',
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        // Handle different possible response structures
        const items =
          result?.data?.items || result?.items || result?.data || result || [];
        const totalItems =
          result?.data?.totalItems || result?.totalItems || items.length;

        setData({
          items: Array.isArray(items) ? items : [],
        });
        setTotalRows(totalItems);
      } catch (error) {
        toast.error(`Failed to load data: ${error.message}`);
        setData({ items: [] });
        setTotalRows(0);
      } finally {
        setLoading(false);
      }
    },
    [] // Removed selectedDocumentType dependency since we're using single endpoint
  );

  // Filter data based on search text and document type
  useEffect(() => {
    let filtered = data.items;

    // Filter by document type
    if (selectedDocumentType !== 'all') {
      filtered = filtered.filter(
        item => item.documentType === parseInt(selectedDocumentType)
      );
    }

    // Filter by search text
    if (filterText) {
      filtered = filtered.filter(
        item =>
          item.documentName?.toLowerCase().includes(filterText.toLowerCase()) ||
          item.description?.toLowerCase().includes(filterText.toLowerCase())
      );
    }

    setFilteredData(filtered);
  }, [data.items, filterText, selectedDocumentType]);

  const handleFilterChange = e => {
    setFilterText(e.target.value);
  };

  const handleDocumentTypeFilterChange = e => {
    setSelectedDocumentType(e.target.value);
  };

  const clearFilter = () => {
    setFilterText('');
    setSelectedDocumentType('all');
  };

  useEffect(() => {
    fetchData(currentPage, rowsPerPage, token);
  }, [currentPage, rowsPerPage, token, fetchData]);

  const handlePageChange = useCallback(page => {
    setCurrentPage(page);
  }, []);

  const handlePerRowsChange = useCallback(newPerPage => {
    setRowsPerPage(newPerPage);
    setCurrentPage(1);
  }, []);

  const handleNewModal = () => {
    setDocumentUploadFormData({
      documentName: null,
      description: null,
      documentType: 1,
      id: null,
      path: null,
    });
    setSelectedFile(null);
    documentUploadModal();
  };

  const handleUpdateModal = (mid = null) => {
    fetchDocumentUploadDetail(mid);
    documentUploadModal();
  };

  const documentUploadModal = () => {
    setModalDocumentUpload(!modalDocumentUpload);
  };

  const handleFileChange = e => {
    setSelectedFile(e.target.files[0] || null);
  };

  const selectedFieldsDocumentUpload = {
    documentName: documentUploadFormData.documentName,
    description: documentUploadFormData.description,
    documentType: documentUploadFormData.documentType,
  };

  const handleAddDataDocumentUpload = async () => {
    try {
      const formData = new FormData();
      formData.append('documentName', documentUploadFormData.documentName);
      formData.append('description', documentUploadFormData.description);
      formData.append('documentType', documentUploadFormData.documentType);
      if (selectedFile) {
        formData.append('File', selectedFile);
      }
      await axios.post(`${api.API_URL}/document-upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      });
      toast.success('Document Upload Master Has been Added');
      setSelectedFile(null);
    } catch (error) {
      toast.error(`Failed to save data: ${error.message}`);
    } finally {
      fetchData(currentPage, rowsPerPage, token);
    }
  };

  const fetchDocumentUploadDetail = async mid => {
    try {
      setSelectedFile(null);
      const response = await axios.get(
        `${api.API_URL}/document-upload/${mid}`,
        {
          withXSRFToken: true,
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: `application/json`,
          },
        }
      );
      const data = response.data || response;
      setDocumentUploadFormData({
        documentName: data.documentName || null,
        description: data.description || null,
        documentType: data.documentType || 1,
        id: data.id || null,
        path: data.path || data.filePath || null,
      });
    } catch (error) {
      toast.error(`Failed to fetch document upload detail: ${error.message}`);
    }
  };

  const handleUpdDataDocumentUpload = async mid => {
    try {
      const formData = new FormData();
      formData.append('documentName', documentUploadFormData.documentName);
      formData.append('description', documentUploadFormData.description);
      formData.append('documentType', documentUploadFormData.documentType);
      if (selectedFile) {
        formData.append('File', selectedFile);
      }
      await axios.put(`${api.API_URL}/document-upload/${mid}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      });
      toast.success('Data Has been Updated');
      setSelectedFile(null);
    } catch (error) {
      toast.error(`Failed to save data: ${error.message}`);
    } finally {
      fetchData(currentPage, rowsPerPage, token);
    }
  };

  return (
    <React.Fragment>
      <div className='page-content'>
        <DeleteModal
          show={deleteModal}
          onDeleteClick={() => handleDeleteInvoice()}
          onCloseClick={() => setDeleteModal(false)}
        />
        <DeleteModal
          show={deleteModalMulti}
          onDeleteClick={() => {
            deleteMultiple();
            setDeleteModalMulti(false);
          }}
          onCloseClick={() => setDeleteModalMulti(false)}
        />
        <Container fluid>
          <BreadCrumb title='View Data' pageTitle='Document Upload Master' />

          <Row>
            <Col lg={12}>
              <Card id='DocumentUploadMasterList'>
                <CardHeader className='border-0'>
                  <div className='d-flex align-items-center'>
                    <h5 className='card-title mb-0 flex-grow-1'>
                      Data Document Upload Master
                    </h5>
                    <div className='flex-shrink-0'>
                      <div className='d-flex gap-2 flex-wrap'>
                        <button
                          type='button'
                          className='btn btn-primary btn-label right ms-auto nexttab btn-sm'
                          onClick={() => handleNewModal()}
                        >
                          <i className='ri-play-list-add-line label-icon align-middle fs-16 ms-2'></i>{' '}
                          <span className='d-none d-sm-inline'>
                            Create New Document Upload Master
                          </span>
                          <span className='d-sm-none'>New</span>
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Filter Section */}
                  <div className='d-flex justify-content-between align-items-center mt-3'>
                    <div className='d-flex align-items-center gap-3'>
                      <Input
                        type='text'
                        placeholder='Search document upload masters...'
                        value={filterText}
                        onChange={handleFilterChange}
                        style={{ width: '300px', height: '31px' }}
                        className='form-control-sm'
                      />
                      <Input
                        type='select'
                        value={selectedDocumentType}
                        onChange={handleDocumentTypeFilterChange}
                        style={{
                          width: '200px',
                          height: '31px',
                          fontSize: '0.75rem',
                          lineHeight: '1.2',
                        }}
                        className='form-control-sm'
                      >
                        <option value='all'>All Document Types</option>
                        <option value='1'>Procurement</option>
                        <option value='2'>HPS</option>
                      </Input>
                      {(filterText || selectedDocumentType !== 'all') && (
                        <Button
                          color='secondary'
                          size='sm'
                          onClick={clearFilter}
                          outline
                          style={{ height: '31px' }}
                        >
                          <i className='ri-close-line'></i> Clear
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>

                <CardBody className='pt-0'>
                  <div className='table-responsive'>
                    <DataTable
                      columns={columns}
                      highlightOnHover
                      data={filteredData}
                      pagination
                      paginationServer={
                        !filterText && selectedDocumentType === 'all'
                      }
                      paginationTotalRows={
                        filterText || selectedDocumentType !== 'all'
                          ? filteredData.length
                          : totalRows
                      }
                      onChangePage={handlePageChange}
                      onChangeRowsPerPage={handlePerRowsChange}
                      showPaginationBottom={true}
                      wrap
                      dense
                      striped
                      progressPending={loading}
                      noDataComponent='No document upload masters found'
                      defaultSortFieldId={1}
                      defaultSortAsc={true}
                      customStyles={{
                        header: {
                          style: {
                            backgroundColor: '#f0f0f0',
                            color: '#333',
                            fontWeight: 'bold',
                          },
                        },
                        headCells: {
                          style: {
                            backgroundColor: '#f0f0f0',
                            fontWeight: 'bold',
                            fontSize: '14px',
                            paddingLeft: '12px',
                            paddingRight: '12px',
                            borderRight: '1px solid #dee2e6',
                          },
                        },
                        cells: {
                          style: {
                            paddingLeft: '12px',
                            paddingRight: '12px',
                            wordBreak: 'break-word',
                            borderRight: '1px solid #dee2e6',
                          },
                        },
                        rows: {
                          style: {
                            minHeight: '50px',
                            '&:hover': {
                              backgroundColor: '#f8f9fa',
                            },
                          },
                        },
                        table: {
                          style: {
                            width: '100%',
                            minWidth: '100%',
                            tableLayout: 'fixed',
                          },
                        },
                        responsiveWrapper: {
                          style: {
                            minHeight: '200px',
                            overflowX: 'hidden',
                          },
                        },
                      }}
                    />

                    <ToastContainer closeButton={false} limit={1} />
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Modal
            isOpen={modalDocumentUpload}
            role='dialog'
            autoFocus={true}
            centered
            id='addDocumentUploadMasterModal'
            toggle={documentUploadModal}
            data-bs-backdrop='static'
            size='md'
          >
            <ModalHeader
              className='bg-soft-info'
              toggle={() => {
                setModalDocumentUpload(!modalDocumentUpload);
              }}
            >
              <span className='modal-title text-info fw-bold'>
                {documentUploadFormData.id !== null
                  ? 'Edit Document Upload Master'
                  : 'Add Document Upload Master'}
              </span>
            </ModalHeader>

            <ModalBody>
              <div className='d-flex mt-2'>
                <Col sm={12}>
                  <Row>
                    <Col sm={12}>
                      <div className='mb-3'>
                        <Label for='document-name' className='form-label'>
                          Document Name :
                        </Label>
                        <div className='input-group'>
                          <Input
                            type='text'
                            id='documentName'
                            name='documentName'
                            className='form-control'
                            value={documentUploadFormData.documentName || ''}
                            onChange={e => {
                              handleChange(e);
                              setErrors({
                                ...errors,
                                documentName: '',
                              });
                            }}
                          />
                        </div>
                      </div>
                    </Col>
                    <Col sm={12}>
                      <div className='mb-3'>
                        <Label htmlFor='description' className='form-label'>
                          Description
                        </Label>
                        <textarea
                          className='form-control'
                          id='description'
                          name='description'
                          rows='3'
                          value={documentUploadFormData.description || ''}
                          onChange={e => {
                            handleChange(e);
                            setErrors({
                              ...errors,
                              description: '',
                            });
                          }}
                        ></textarea>
                      </div>
                    </Col>
                    <Col sm={12}>
                      <div className='mb-3'>
                        <Label htmlFor='documentType' className='form-label'>
                          Document Type
                        </Label>
                        <Input
                          type='select'
                          id='documentType'
                          name='documentType'
                          className='form-control'
                          value={documentUploadFormData.documentType || 1}
                          onChange={e => {
                            handleChange(e);
                            setErrors({
                              ...errors,
                              documentType: '',
                            });
                          }}
                        >
                          {documentTypeOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Input>
                      </div>
                    </Col>
                    <Col sm={12}>
                      <div className='mb-3'>
                        <Label htmlFor='fileUpload' className='form-label'>
                          File Attachment
                        </Label>
                        {documentUploadFormData.id !== null &&
                        documentUploadFormData.path ? (
                          <div className='d-flex align-items-center gap-2 mb-2 p-2 border rounded bg-light'>
                            <i className='ri-file-text-line fs-16'></i>
                            <span className='text-truncate flex-grow-1 small'>
                              {documentUploadFormData.path.split('/').pop() ||
                                documentUploadFormData.path}
                            </span>
                            <Button
                              color='success'
                              size='sm'
                              outline
                              className='btn-sm'
                              onClick={() =>
                                handleViewPath(documentUploadFormData.path)
                              }
                            >
                              <i className='ri-eye-fill me-1'></i> View
                            </Button>
                          </div>
                        ) : null}
                        <Input
                          type='file'
                          id='fileUpload'
                          name='fileUpload'
                          innerRef={fileInputRef}
                          onChange={handleFileChange}
                        />
                        {selectedFile && (
                          <small className='text-muted d-block mt-1'>
                            <i className='ri-file-text-line me-1'></i>
                            {selectedFile.name} (
                            {(selectedFile.size / 1024).toFixed(1)} KB)
                          </small>
                        )}
                      </div>
                    </Col>
                  </Row>
                </Col>
              </div>
            </ModalBody>

            <ModalFooter>
              <Button
                color='danger'
                className='btn-label btn-sm right'
                onClick={() => {
                  setModalDocumentUpload(!modalDocumentUpload);
                }}
              >
                {' '}
                <i className=' ri-close-circle-line label-icon align-middle fs-16 ms-2'></i>{' '}
                Close
              </Button>
              <Button
                color='primary'
                className='btn-label btn-sm right'
                onClick={() => {
                  setModalDocumentUpload(!modalDocumentUpload);
                  documentUploadFormData.id !== null
                    ? handleUpdDataDocumentUpload(documentUploadFormData.id)
                    : handleAddDataDocumentUpload();
                }}
              >
                {' '}
                <i className='ri-database-2-line label-icon align-middle fs-16 ms-2'></i>{' '}
                {documentUploadFormData.id !== null ? 'Update' : 'Save'}
              </Button>
            </ModalFooter>
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default DocumentUploadMasterList;
